import React, {useState, useRef, useEffect} from "react";
import {
  LevelIndicator,
  LupaCircle,
  RightBarContainer,
  SliderContainer,
  VolumeControl,
  VolumeLevel,
} from "./RadarChart.styles";
import IconEyeClose from "../../assets/icons/filters/eye-close.svg";
import IconEyeOpen from "../../assets/icons/filters/IconEye.svg";
import IconPlus from "../../assets/icons/filters/circle-plus.svg";
import IconMinus from "../../assets/icons/filters/circle-minus.svg";
import {Tooltip} from "react-tooltip";
import styled from "styled-components";

const StyledSliderContainer = styled(SliderContainer)`
  position: relative;
  width: 40px;
  height: 160px;
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

const StyledLevelIndicator = styled(LevelIndicator)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  z-index: 1;
`;

const StyledLupaCircle = styled(LupaCircle)<{position: "top" | "bottom"}>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  ${({position}) =>
    position === "top" ? "top: 0;" : position === "bottom" ? "bottom: 0;" : ""}
`;

interface ZoomableBlockProps {
  children: React.ReactNode;
  focusPosition: {x: number; y: number} | null;
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  isOpenPanel?: boolean;
  isOpenSideBarInfo?: boolean;
}

const ZoomableBlock: React.FC<ZoomableBlockProps> = ({
  children,
  focusPosition,
  scale,
  setScale,
  isOpenPanel,
  isOpenSideBarInfo,
}) => {
  const [isDragging, setDragging] = useState(false);
  const [position, setPosition] = useState({x: 0, y: 0});
  const [level, setLevel] = useState(~~((scale - 1) * 2.25));
  // const [isDarkMode, setIsDarkMode] = useState(false);
  const blockRef = useRef<HTMLDivElement | null>(null);
  const start = useRef({x: 0, y: 0});
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [isDragging2, setIsDragging2] = useState(false);
  const containerRef = useRef(null);
  const [volume, setVolume] = useState(2);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({width: window.innerWidth, height: window.innerHeight});
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    if (!blockRef.current) return;

    requestAnimationFrame(() => {
      if (!blockRef.current) return;

      const rect = blockRef.current.getBoundingClientRect();

      const offsetX = (event.clientX - rect.left) / scale;
      const offsetY = (event.clientY - rect.top) / scale;

      const newScale = Math.min(Math.max(scale + event.deltaY * -0.01, 1), 5);
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      let newPosition = {
        x: 0,
        y: 0,
      };
      let newPositionX = 0;
      let newPositionY = 0;

      const centerRadarX = rect.left + rect.width / 2;
      const centerRadarY = rect.top + rect.height / 2;

      if (newScale >= scale) {
        if (event.x > centerRadarX) {
          newPositionX = -(event.x - centerRadarX);
        } else if (event.x < centerRadarX) {
          newPositionX = (centerRadarX - event.x) * (centerRadarX - event.x);
        } else {
          newPositionX = rect.x;
        }

        if (event.y > centerRadarY) {
          newPositionY = -(event.y - centerRadarY);
        } else if (event.y < centerRadarY) {
          newPositionY = (centerRadarY - event.y) * (centerRadarY - event.y);
        } else {
          newPositionY = rect.y;
        }
      } else {
        if (event.x < centerRadarX) {
          newPositionX = -(event.x - centerRadarX) / 6;
        } else if (event.x > centerRadarX) {
          newPositionX = -(event.x - centerRadarX) / 6;
        } else {
          newPositionX = rect.x;
        }

        if (event.y < centerRadarY) {
          newPositionY = (event.y - centerRadarY) / 30;
        } else if (event.y > centerRadarY) {
          newPositionY = -(event.y - centerRadarY) / 30;
        } else {
          newPositionY = rect.y;
        }
      }
      newPosition = {x: newPositionX, y: newPositionY};
      if (newScale === 1) {
        newPosition = {x: 0, y: 0};
      }

      setScale(newScale);
      setPosition(clampPosition(newPosition, newScale));

      let minVolume = 2;
      let maxVolume = 100;
      let volumeStep = (maxVolume - minVolume) / 4;
      volumeStep = 20;

      let delta = event.deltaY < 0 ? volumeStep : -volumeStep;

      let newVolume = Math.max(minVolume, Math.min(maxVolume, volume + delta));

      setVolume(newVolume);
    });
  };

  useEffect(() => {
    if (focusPosition && blockRef.current) {
      const widthSidebarCard = 640;
      const newScale = 3;
      const xPosition =
        dimensions.width / newScale -
        dimensions.width / 2 -
        focusPosition.x * newScale +
        widthSidebarCard;
      const yPosition =
        dimensions.height / newScale -
        dimensions.height / newScale -
        focusPosition.y * newScale;

      const newPosition = {
        x: xPosition,
        y: yPosition,
      };
      setScale(newScale);
      setPosition(newPosition);
      setVolume(newScale * 20);
      console.log("focusPosition", focusPosition);
    }
  }, [focusPosition]);

  const handleMouseDown = (event: React.MouseEvent) => {
    setDragging(true);
    start.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    };
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    setDragging(true);
    start.current = {
      x: event.touches[0].clientX - position.x,
      y: event.touches[0].clientY - position.y,
    };
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (isDragging) {
      const newPosition = {
        x: event.touches[0].clientX - start.current.x,
        y: event.touches[0].clientY - start.current.y,
      };
      setPosition(clampPosition(newPosition, scale));
    }
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    setDragging(false);
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    setDragging(false);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging) {
      const newPosition = {
        x: event.clientX - start.current.x,
        y: event.clientY - start.current.y,
      };
      setPosition(clampPosition(newPosition, scale));
    }
  };

  // const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const newScale = Number(event.target.value);
  //   setScale(newScale);
  //   if (newScale === 1) {
  //     setPosition({x: 0, y: 0});
  //   } else {
  //     setPosition(clampPosition(position, newScale));
  //   }
  // };

  const clampPosition = (position: {x: number; y: number}, scale: number) => {
    if (!blockRef.current) return position;
    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;

    const rect = blockRef.current.getBoundingClientRect();
    const scaledWidth = rect.width * scale;
    const scaledHeight = rect.height * scale;

    const xMoveMax = scale < 3 ? 10 : scale < 4 ? 13 : 15;
    const yMoveMax = scale < 3 ? 7 : scale < 4 ? 10 : 12;

    const maxX = (scaledWidth - containerWidth) / (xMoveMax / 1.5);
    const maxY = (scaledHeight - containerHeight) / yMoveMax;

    return {
      x: Math.min(Math.max(position.x, -maxX), maxX),
      y: Math.min(Math.max(position.y, -maxY), maxY),
    };
  };

  useEffect(() => {
    const block = blockRef.current;
    if (block) {
      block.style.transform = `translate(${position.x}px, ${position.y}px) scale(${scale})`;
    }
  }, [position, scale]);

  useEffect(() => {
    const block = blockRef.current;
    if (block) {
      const wheelHandler = (event: WheelEvent) => handleWheel(event);
      block.addEventListener("wheel", wheelHandler, {passive: false});
      return () => {
        block.removeEventListener("wheel", wheelHandler);
      };
    }
  }, [scale]);

  // const handleScaleUp = () => {
  //   if (scale >= 5) return;
  //   setScale(scale + 0.1);
  // };

  // const handleScaleDown = () => {
  //   if (scale <= 1) return;
  //   setScale(scale - 0.1);
  // };

  useEffect(() => {
    const handleWindowMouseUp = () => {
      setDragging(false);
    };

    const handleWindowMouseMove = (event: MouseEvent) => {
      if (isDragging) {
        const newPosition = {
          x: event.clientX - start.current.x,
          y: event.clientY - start.current.y,
        };
        setPosition(clampPosition(newPosition, scale));
      }
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    window.addEventListener("mousemove", handleWindowMouseMove);

    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
      window.removeEventListener("mousemove", handleWindowMouseMove);
    };
  }, [isDragging, scale]);

  useEffect(() => {
    setLevel(Math.round((scale - 1) * 7.25));
  }, [scale]);

  const handleLevelChange = (newLevel: number) => {
    setLevel(newLevel);
    setScale(1 + newLevel / 7.25);
  };
  ///////////////////////////////////////////////////
  const handleMouseDown2 = (e: React.MouseEvent) => {
    // if (!isDragging2) return;
    if (!containerRef.current) return;

    requestAnimationFrame(() => {
      if (!containerRef.current) return;

      const containerRect = (
        containerRef.current as Element
      ).getBoundingClientRect();
      const offsetY = containerRect.bottom - e.clientY;
      const newVolume = Math.max(
        0,
        Math.min(100, Math.floor((offsetY / containerRect.height) * 100))
      );

      setVolume(newVolume);
      const newScale = 1 + (newVolume / 100) * 4;
      setScale(newScale);
      setIsDragging2(true);
    });
  };

  const handleMouseUp2 = () => {
    setIsDragging2(false);
  };

  const handleMouseMove2 = (e: React.MouseEvent) => {
    if (!isDragging2) return;
    if (!containerRef.current) return;

    requestAnimationFrame(() => {
      if (!containerRef.current || !isDragging2) return;

      const containerRect = (
        containerRef.current as Element
      ).getBoundingClientRect();
      const offsetY = containerRect.bottom - e.clientY;
      const newVolume = Math.max(
        0,
        Math.min(100, Math.floor((offsetY / containerRect.height) * 100))
      );

      setVolume(newVolume);
      const newScale = 1 + (newVolume / 100) * 4;
      setScale(newScale);
    });
  };

  useEffect(() => {
    if (!isDragging2) return;
    const onMouseUp = () => handleMouseUp2();
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [isDragging2]);

  return (
    <div
      style={{
        overflow: "hidden",
        width: "100vw",
        height: "100vh",
        position: "relative",
      }}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <div
        ref={blockRef}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          // transition: "transform 0.05s ease-in-out",
          userSelect: "none",
          overflow: "hidden",
          paddingLeft: isOpenPanel ? "150px" : "0px",
          transition: "all 0.3s ease",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>

      <RightBarContainer>
        {/* <ShowClastersContainer
          data-tooltip-id="eye"
          data-tooltip-content="ПОКАЗАТЬ НАЗВАНИЯ КЛАСТЕРОВ"
          data-tooltip-place="left"
          onClick={() => setShowAllLabels(!showAllLabels)}
          style={{
            backgroundColor: showAllLabels ? "#0082ED" : "#272b40",
            borderColor: showAllLabels ? "#ffffff" : "#4e5574",
          }}
        >
          <img
            src={showAllLabels ? IconEyeOpen : IconEyeClose}
            alt="eye"
            style={{
              transition: "filter 0.3s ease",
            }}
          />
          <Tooltip
            id="eye"
            className="tooltip"
            noArrow
            offset={4}
            place="top"
          />
        </ShowClastersContainer> */}
        {/* <StyledSliderContainer> */}
        {/* <StyledLupaCircle
            position="top"
            disabled={scale >= 5}
            style={{top: 0}}
          >
            <img src={IconPlus} alt="light" />
          </StyledLupaCircle> */}

        {/* <StyledLevelIndicator
            level={level}
            onMouseDown={(e) => {
              const rect = (e.target as HTMLDivElement).getBoundingClientRect();
              const updateLevel = (clientY: number) => {
                const clickY = clientY - rect.top;
                // 30 делений, верхняя граница совпадает с мышью
                const newLevel = Math.max(
                  0,
                  Math.min(
                    30,
                    Math.floor(((rect.height - clickY) / rect.height) * 30)
                  )
                );
                handleLevelChange(newLevel);
              };

              updateLevel(e.clientY);

              const onMouseMove = (moveEvent: MouseEvent) => {
                updateLevel(moveEvent.clientY);
              };
              const onMouseUp = () => {
                window.removeEventListener("mousemove", onMouseMove);
                window.removeEventListener("mouseup", onMouseUp);
              };
              window.addEventListener("mousemove", onMouseMove);
              window.addEventListener("mouseup", onMouseUp);
            }}
            style={{cursor: "pointer"}}
          >
            <LevelFill level={level} />
          </StyledLevelIndicator> */}
        {/* <StyledLupaCircle
            position="bottom"
            data-tooltip-id="slider"
            data-tooltip-content="МАСШТАБИРОВАНИЕ РАДАРА"
            data-tooltip-place="left"
            disabled={scale <= 1}
            style={{bottom: 10, color: "#fff", fontSize: 14}}
          >
            {Math.round(scale)}x{/* <img src={IconMinus} alt="light" /> */}
        {/* </StyledLupaCircle> */}
        {/* </StyledSliderContainer> */}

        <VolumeControl
          ref={containerRef}
          className="volume-control"
          onMouseDown={handleMouseDown2}
          onMouseUp={handleMouseUp2}
          onMouseMove={handleMouseMove2}
        >
          <StyledLupaCircle position="top" style={{top: 0}}>
            <img src={IconPlus} alt="light" />
          </StyledLupaCircle>
          <VolumeLevel
            className="volume-level"
            style={{height: `${volume}%`}}
          />
          <StyledLupaCircle
            position="bottom"
            data-tooltip-id="slider"
            data-tooltip-content="МАСШТАБИРОВАНИЕ РАДАРА"
            data-tooltip-place="left"
            style={{bottom: 10, color: "#fff", fontSize: 14, left: "60%"}}
          >
            {Math.round(scale)}x
          </StyledLupaCircle>
        </VolumeControl>
      </RightBarContainer>
    </div>
  );
};

export default ZoomableBlock;
